import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, Col, Form, Layout, Radio, Row } from 'antd';
import { CheckboxOptionType, RadioChangeEvent, Spin } from 'antd/lib';
import dayjs from 'dayjs';
import { FC, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  CreateReportDto,
  CreateReportDtoTypeEnum,
  GetEmployeeTimekeepingHistoryDto,
  GetEmployeeTimekeepingHistoryDtoScheduleTypeEnum,
  GetEmployeeTimekeepingHistoryDtoWorkTypeEnum,
  Option,
  OptionCategoryEnum,
  ScheduleSettingTypeEnum,
  TimekeepingReport,
  TimekeepingReportTypeEnum,
  UpdateReportDto,
} from '../../../src/apis/client-axios';
import './style.scss';
import { GENERAL_CONFIG_API, QUERY_KEY, REPORT_API } from '../../constants/QueryKey';
import { DATE_FORMAT, DATE_FORMAT_JP_2, TIME_FORMAT_JP, YEAR_MONTH_DATE_FORMAT_JP } from '../../constants/common';
import ToastSuccess from '../Toast/ToastSuccess';
import ToastError from '../Toast/ToastError';
import ModalWrap from '../ModalWrap';
import { generalConfigApi, reportApi, schedulesApi } from '../../apis';
import ButtonWrap from '../ButtonWrap';
import TableWrap from '../TableWrap';
import { statusMapping } from '../../utils';
import { ColumnsType, ColumnType } from 'antd/es/table';
import { AnyObject } from 'antd/es/_util/type';
import SvgEdit from '../svg/SvgEdit';
import ModalReport from './ModalReport';
import ModalDeleteReport from './ModalDeleteReport';
import SvgDelete from '../svg/SvgDelete';

interface ModalTimeKeepingProps {
  open: boolean;
  timeKeepingHistory: GetEmployeeTimekeepingHistoryDto;
  onCancel: () => void;
  onSubmit: () => void;
}

interface IModalReport {
  open: boolean;
  timekeepingReport?: TimekeepingReport;
  type?: TimekeepingReportTypeEnum;
}

const ModalTimeKeeping: FC<ModalTimeKeepingProps> = (props) => {
  const { open, timeKeepingHistory, onCancel, onSubmit } = props;
  const intl = useIntl();
  const [modalReport, setModalReport] = useState<IModalReport>({ open: false });
  const [modalDeleteReport, setModalDeleteReport] = useState<IModalReport>({ open: false });

  const { data: optionData, isLoading: isLoadingOption } = useQuery({
    queryKey: [GENERAL_CONFIG_API],
    queryFn: () =>
      generalConfigApi.generalConfigControllerGetOptions(
        undefined,
        undefined,
        dayjs(timeKeepingHistory.date, YEAR_MONTH_DATE_FORMAT_JP).format(DATE_FORMAT),
        timeKeepingHistory.userId,
        true,
      ),
  });

  const {
    data: reportsData,
    isLoading: isLoadingReports,
    refetch: refetchReports,
  } = useQuery({
    queryKey: [REPORT_API],
    queryFn: () =>
      reportApi.reportControllerGetListReport(
        dayjs(timeKeepingHistory.date, DATE_FORMAT_JP_2).format(DATE_FORMAT),
        dayjs(timeKeepingHistory.date, DATE_FORMAT_JP_2).format(DATE_FORMAT),
        1,
        timeKeepingHistory.userId,
        undefined,
        undefined,
      ),
  });

  const createReportMutation = useMutation({
    mutationFn: (payload: CreateReportDto) => {
      return reportApi.reportControllerCreate({ ...payload, isRequestedFromWeb: true });
    },
    onSuccess: () => {
      ToastSuccess({
        content: intl.formatMessage({ id: 'timekeepingReports.create.success' }),
      });
      onSubmit();
    },
    onError: (error: any) => {
      let content = intl.formatMessage({ id: 'timekeepingReports.create.error' });
      if (intl.messages[`timekeepingReports.error.${error.response.data.errorCode}`]) {
        content = intl.formatMessage({ id: `timekeepingReports.error.${error.response.data.errorCode}` });
      }
      ToastError({
        content,
      });
    },
  });

  const updateReportMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateReportDto }) => {
      return reportApi.reportControllerUpdateReport(id, { ...payload, isRequestedFromWeb: true });
    },
    onSuccess: () => {
      ToastSuccess({
        content: intl.formatMessage({ id: 'timekeepingReports.update.success' }),
      });
      onSubmit();
    },
    onError: (error: any) => {
      let content = intl.formatMessage({ id: 'timekeepingReports.create.error' });
      if (intl.messages[`timekeepingReports.error.${error.response.data.errorCode}`]) {
        content = intl.formatMessage({ id: `timekeepingReports.error.${error.response.data.errorCode}` });
      }
      ToastError({
        content,
      });
    },
  });

  const deleteReportMutation = useMutation({
    mutationFn: (id: string) => {
      return reportApi.reportControllerDeleteReport(id);
    },
    onSuccess: () => {
      ToastSuccess({
        content: intl.formatMessage({ id: 'timekeepingReports.delete.success' }),
      });
      onSubmit();
    },
    onError: (error: any) => {
      ToastError({
        content: intl.formatMessage({ id: 'timekeepingReports.delete.error' }),
      });
    },
  });

  const listActionButtons: { value: TimekeepingReportTypeEnum; span: number }[] = [
    { value: TimekeepingReportTypeEnum.RequestTimekeepingEdit, span: 24 },
    { value: TimekeepingReportTypeEnum.GoingLate, span: 8 },
    { value: TimekeepingReportTypeEnum.LeavingSoon, span: 8 },
    { value: TimekeepingReportTypeEnum.Overtime, span: 8 },
    { value: TimekeepingReportTypeEnum.OutsidePrivate, span: 12 },
    { value: TimekeepingReportTypeEnum.OutsideBusiness, span: 12 },
    { value: TimekeepingReportTypeEnum.RequestDayOff, span: 12 },
    { value: TimekeepingReportTypeEnum.RequestBusiness, span: 12 },
  ];

  const columns: ColumnType<TimekeepingReport>[] = [
    {
      title: intl.formatMessage({ id: 'application.approval.column.distinction' }),
      dataIndex: 'type',
      key: 'type',
      render: (value) => {
        return (
          <p className="employee-option_table-column-data">
            {intl.formatMessage({ id: `timekeepingReports.${value}` })}
          </p>
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'application.approval.column.time' }),
      dataIndex: 'createdOnDate',
      key: 'createdOnDate',
      render: (value) => {
        return <p className="employee-option_table-column-data">{dayjs(value).format(TIME_FORMAT_JP)}</p>;
      },
    },
    {
      title: intl.formatMessage({ id: 'application.approval.column.status' }),
      dataIndex: 'status',
      key: 'status',
      render: (value) => {
        const { class: statusClass, intlText } = statusMapping[value] || statusMapping.default;
        const statusText = intlText ? intl.formatMessage({ id: intlText }) : '';
        return <p className={'employee-option_table-column-data'}>{statusText}</p>;
      },
    },
    {
      title: '',
      width: 200,
      render: (value, record) => {
        return (
          <Row gutter={[12, 12]}>
            <Col span={12}>
              <ButtonWrap
                onClick={() => setModalReport({ open: true, timekeepingReport: record, type: record.type })}
                className="attendance-confirm_table-column-button"
              >
                <SvgEdit />
                {intl.formatMessage({ id: 'common.edit' })}
              </ButtonWrap>
            </Col>
            <Col span={12}>
              <ButtonWrap
                onClick={() => setModalDeleteReport({ open: true, timekeepingReport: record })}
                className="attendance-confirm_table-column-button"
              >
                <SvgDelete />
                {intl.formatMessage({ id: 'common.delete' })}
              </ButtonWrap>
            </Col>
          </Row>
        );
      },
    },
  ];

  const onClickActionButton = (type: TimekeepingReportTypeEnum) => {
    let report: TimekeepingReport | undefined;
    if (
      (
        [
          TimekeepingReportTypeEnum.RequestCheckin,
          TimekeepingReportTypeEnum.RequestCheckout,
          TimekeepingReportTypeEnum.GoingLate,
          TimekeepingReportTypeEnum.LeavingSoon,
          TimekeepingReportTypeEnum.Overtime,
          TimekeepingReportTypeEnum.RequestTimekeepingEdit,
          TimekeepingReportTypeEnum.RequestDayOff,
          TimekeepingReportTypeEnum.RequestBusiness,
        ] as TimekeepingReportTypeEnum[]
      ).includes(type)
    ) {
      report = reportsData?.data.data?.content?.find((report) => report.type === type);
    }
    setModalReport({ open: true, timekeepingReport: report, type });
  };

  const onCancelModalReport = () => {
    setModalReport({ open: false, timekeepingReport: undefined, type: undefined });
  };

  const onSubmitModalReport = (data: CreateReportDto | UpdateReportDto, id?: string) => {
    if (id) {
      updateReportMutation.mutate({ id, payload: data as UpdateReportDto });
    } else {
      createReportMutation.mutate(data as CreateReportDto);
    }
  };

  const onCancelModalDeleteReport = () => {
    setModalDeleteReport({ open: false, timekeepingReport: undefined });
  };

  const onSubmitModalDeleteReport = (id: string) => {
    deleteReportMutation.mutate(id);
  };

  return (
    <ModalWrap
      open={open}
      cancelText={intl.formatMessage({ id: 'attendance.confirm.modal.button.cancel' })}
      submitText={intl.formatMessage({ id: 'attendance.confirm.modal.button.submit' })}
      onSubmit={() => {}}
      wrapClassName="attendance-confirm_modal"
      onCancel={onCancel}
      type="upsert"
      title={intl.formatMessage({ id: 'attendance.confirm.modal.title' })}
      destroyOnClose
      hideSubmitText
      body={
        <Layout className="bg-white">
          <Row gutter={[16, 16]}>
            {listActionButtons.map((btn) => (
              <Col span={btn.span} key={btn.value}>
                <ButtonWrap
                  variant="outlined"
                  color="black"
                  width={'full'}
                  onClick={() => onClickActionButton(btn.value)}
                >
                  {intl.formatMessage({ id: `timekeepingReports.${btn.value}` })}
                </ButtonWrap>
              </Col>
            ))}
          </Row>

          <TableWrap
            isPagination={false}
            loading={isLoadingReports}
            tableProps={{
              dataSource: reportsData?.data.data?.content,
              columns: columns as ColumnsType<AnyObject>,
              className: 'attendance-confirm_table mt-4',
            }}
          />

          {modalReport.open && modalReport.type && (
            <ModalReport
              open={modalReport.open}
              timekeepingReport={modalReport.timekeepingReport}
              onCancel={onCancelModalReport}
              onSubmit={onSubmitModalReport}
              type={modalReport.type}
              options={optionData?.data.data || []}
              timeKeepingHistory={timeKeepingHistory}
              isLoading={createReportMutation.isPending || updateReportMutation.isPending}
            />
          )}

          {modalDeleteReport.open && modalDeleteReport.timekeepingReport && (
            <ModalDeleteReport
              open={modalDeleteReport.open}
              timekeepingReport={modalDeleteReport.timekeepingReport}
              onCancel={onCancelModalDeleteReport}
              onSubmit={onSubmitModalDeleteReport}
              isLoading={deleteReportMutation.isPending}
            />
          )}
        </Layout>
      }
    />
  );
};

export default ModalTimeKeeping;
